<script>
import useSharedSelectData from "@/use/SharedSelectData";
import {onMounted, ref, watch} from "vue";
import StrDurationAutocomplete from "@/views/ContratTemplate/Components/StrDurationAutocomplete.vue";

export default {
    props: {
        value: Object,
    },
    components: {StrDurationAutocomplete},
    setup(props, {emit}) {
        const {workerPhases, contratStatus, contratTrialPeriodStatus} = useSharedSelectData();
        const panelModel = ref(0);
        
        const filter = ref({});
        
        watch(() => filter.value, () => {
            emit('input', filter.value);
        }, {deep: true});
        
        
        const duration = ref({});
        watch(() => duration.value, () => {
            if (duration.value) {
                filter.value = {
                    ...filter.value,
                    duration: duration.value.duration,
                    periodicity: duration.value.periodicity
                }
            } else {
                filter.value = {...filter.value, duration: null, periodicity: null}
            }
        });
        
        const trialDuration = ref({});
        watch(() => trialDuration.value, () => {
            if (trialDuration.value) {
                filter.value = {
                    ...filter.value,
                    trialPeriodDuration: trialDuration.value.duration,
                    trialPeriodPeriodicity: trialDuration.value.periodicity
                }
            } else {
                filter.value = {...filter.value, trialPeriodDuration: null, trialPeriodPeriodicity: null}
            }
        });
        
        onMounted(() => {
            filter.value = props.value;
        })
        
        
        return {
            filter,
            workerPhases,
            contratStatus,
            contratTrialPeriodStatus,
            duration,
            trialDuration,
            panelModel
        }
    },
};
</script>

<template>
    <div>
        <v-expansion-panels v-model="panelModel" dense focusable>
            <v-expansion-panel>
                <v-expansion-panel-header>
                    <strong>Contrat</strong>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="mr-n5 ml-n5" eager>
                    <v-container fluid>
                        <v-row dense>
                            <v-col cols="12">
                                <w-text-field
                                    v-model="filter.number"
                                    :label="$t('contrat.number')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <str-worker-autocomplete
                                    v-model="filter.workerId"
                                    :label="$t('module.worker')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <str-contrat-template-autocomplete
                                    v-model="filter.contratTemplateId"
                                    :label="$t('common.modal')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-enum-select
                                    v-model="filter.determinedTime"
                                    :label="$t('common.type')"
                                    :items="[
                                        { id: true, name: 'CDD' },
                                        { id: false, name: 'CDI' }
                                    ]"
                                />
                            </v-col>
                            <v-col cols="12">
                                <str-duration-autocomplete
                                    v-model="duration"
                                    :label="$t('contrat.duration')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-data-select-autocomplete
                                    v-model="filter.natureId"
                                    :label="$t('module.contrat_characteristic')"
                                    type="CONTRAT_NATURE"
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-enum-select
                                    v-model="filter.status"
                                    :items="contratStatus"
                                    :label="$t('common.state')"
                                    multiple
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-enum-select
                                    v-model="filter.trialPeriodStatus"
                                    :items="contratTrialPeriodStatus"
                                    :label="$t('contrat.trial_period')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <strong>{{ $t('contrat.termination') }}</strong>
                            </v-col>
                            <v-col cols="12">
                                <w-data-select-autocomplete
                                    v-model="filter.exitRationaleId"
                                    :label="$t('module.contrat_leaving_reasons')"
                                    color="red"
                                    type="CONTRAT_EXIT_RATIONALE"
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-date-picker
                                    v-model="filter.leavingStart"
                                    :label="$t('common.from')"
                                    :color="'red'"
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-date-picker
                                    v-model="filter.leavingEnd"
                                    :label="$t('common.to')"
                                    :color="'red'"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
                <v-expansion-panel-header>
                    <strong>{{ $t('module.worker') }}</strong>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="mr-n5 ml-n5" eager>
                    <v-container fluid>
                        <v-row dense>
                            <v-col cols="12">
                                <organizational-chart-select
                                    v-model="filter.organizationalchartIds"
                                    :label="$t('module.organizational_chart')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <str-occupation-autocomplete
                                    v-model="filter.occupationId"
                                    :label="$t('module.occupation')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-data-select-autocomplete
                                    v-model="filter.workplaceId"
                                    type="WORKER_WORKPLACE"
                                    show-default-label
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-data-select-autocomplete
                                    v-model="filter.companyId"
                                    type="WORKER_COMPANY"
                                    show-default-label
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-data-select-autocomplete
                                    v-model="filter.stateId"
                                    type="WORKER_STATE"
                                    show-default-label
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-enum-select
                                    v-model="filter.phase"
                                    :items="workerPhases"
                                    :label="$t('worker.status')"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
                <v-expansion-panel-header>
                    <strong>{{ $t('common.other') }}</strong>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="mr-n5 ml-n5" >
                    <v-container fluid>
                        <v-row dense>
                            <v-col cols="12">
                                <w-control-program-autocomplete
                                    v-model="filter.programIds"
                                    module="contrat"
                                    :label="$t('module.control_program')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-issue-autocomplete
                                    v-model="filter.issueIds"
                                    module="contrat"
                                    :label="$t('module.issue')"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>
        
        <input type="hidden"/>
    </div>
</template>